import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production you could send this to a logging service like Sentry
    console.error('MyBalance ErrorBoundary caught:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 px-4">
          {/* Background blobs */}
          <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-purple-500/20 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />
          </div>

          <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/90 text-center">
            {/* Icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30">
              <span className="text-4xl">⚠️</span>
            </div>

            {/* Message */}
            <h1 className="mb-2 text-2xl font-black text-slate-900 dark:text-white">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              MyBalance ran into an unexpected error. Your data is safe - this is a display issue only. Please try refreshing the page.
            </p>

            {/* Error details (dev-friendly, collapsed) */}
            {this.state.error && (
              <details className="mb-6 rounded-xl bg-slate-50 dark:bg-slate-700/50 p-3 text-left">
                <summary className="cursor-pointer text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Error details
                </summary>
                <pre className="mt-2 overflow-x-auto text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={this.handleReload}
                className="flex-1 min-h-[44px] rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-90 transition-all active:scale-95"
              >
                🔄 Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 min-h-[44px] rounded-xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all active:scale-95 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                🏠 Go to Dashboard
              </button>
            </div>

            {/* Support link */}
            <p className="mt-6 text-xs text-slate-400 dark:text-slate-500">
              If this keeps happening, contact us at{' '}
              <a href="mailto:contact@mybalancya.com" className="text-blue-500 hover:underline">
                contact@mybalancya.com
              </a>
            </p>

            {/* Branding */}
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-400">
                MyBalance by{' '}
                <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Unova
                </span>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
